/**
 * Evo Design System Theme for Approval Components
 * Uses Evo's color palette with Netflix-inspired UX patterns
 */

export const approvalTheme = {
  // Color Palette - Using Evo Design System CSS Variables
  colors: {
    // Backgrounds
    bg: {
      primary: 'hsl(var(--background))',      // Main background
      secondary: 'hsl(var(--card))',          // Card background
      tertiary: 'hsl(var(--muted))',          // Elevated elements
      hover: 'hsl(var(--accent))',            // Hover states
      overlay: 'hsl(var(--background) / 0.8)', // Modal overlay
    },
    
    // Text
    text: {
      primary: 'hsl(var(--foreground))',      // Primary text
      secondary: 'hsl(var(--muted-foreground))', // Secondary text
      tertiary: 'hsl(var(--muted-foreground) / 0.7)', // Muted text
      disabled: 'hsl(var(--muted-foreground) / 0.5)', // Disabled text
    },
    
    // Accents - Using Evo's accent system
    accent: {
      primary: 'hsl(var(--primary))',         // Primary CTA
      primaryHover: 'hsl(var(--primary) / 0.9)', // Hover state
      secondary: 'hsl(var(--accent-info-500))', // Blue (info)
      success: 'hsl(var(--accent-success-500))', // Green (success)
      warning: 'hsl(var(--accent-warning-500))', // Orange (warning)
      error: 'hsl(var(--accent-destructive-500))', // Red (error)
    },
    
    // Borders
    border: {
      default: 'hsl(var(--border))',          // Default border
      hover: 'hsl(var(--border) / 0.8)',      // Hover border
      focus: 'hsl(var(--ring))',              // Focus border
    },
    
    // Status Colors - Using Evo's accent system
    status: {
      pending: 'hsl(var(--accent-warning-500))',      // Orange
      approved: 'hsl(var(--accent-success-500))',     // Green
      rejected: 'hsl(var(--accent-destructive-500))', // Red
      draft: 'hsl(var(--muted-foreground))',          // Gray
      active: 'hsl(var(--accent-info-500))',          // Blue
      inactive: 'hsl(var(--muted-foreground) / 0.5)', // Dark gray
    },
  },

  // Typography
  typography: {
    // Headings
    h1: {
      fontSize: '2.5rem',      // 40px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',        // 32px
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',      // 24px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',     // 20px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    
    // Body
    body: {
      fontSize: '1rem',        // 16px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    bodyLarge: {
      fontSize: '1.125rem',    // 18px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    bodySmall: {
      fontSize: '0.875rem',    // 14px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    
    // Labels
    label: {
      fontSize: '0.875rem',    // 14px
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
  },

  // Spacing
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
    '4xl': '6rem',    // 96px
  },

  // Border Radius - Using Evo's radius system
  radius: {
    sm: 'calc(var(--radius) - 4px)',
    md: 'calc(var(--radius) - 2px)',
    lg: 'var(--radius)',
    xl: 'calc(var(--radius) + 4px)',
    full: '9999px',
  },

  // Shadows - Using Evo's shadow system
  shadows: {
    sm: 'var(--shadow-sm)',
    md: 'var(--shadow-md)',
    lg: 'var(--shadow-lg)',
    xl: 'var(--shadow-xl)',
    glow: '0 0 20px hsl(var(--primary) / 0.5)',
  },

  // Animations
  animations: {
    // Durations
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
    },
    
    // Easing
    easing: {
      default: 'cubic-bezier(0.4, 0, 0.2, 1)',
      in: 'cubic-bezier(0.4, 0, 1, 1)',
      out: 'cubic-bezier(0, 0, 0.2, 1)',
      inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  // Z-Index
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    modal: 1200,
    popover: 1300,
    tooltip: 1400,
  },
} as const;

export type ApprovalTheme = typeof approvalTheme;

// CSS-in-JS helper for transitions
export const transition = (
  property: string | string[],
  duration: keyof typeof approvalTheme.animations.duration = 'normal',
  easing: keyof typeof approvalTheme.animations.easing = 'default'
) => {
  const props = Array.isArray(property) ? property : [property];
  return props
    .map(
      (prop) =>
        `${prop} ${approvalTheme.animations.duration[duration]} ${approvalTheme.animations.easing[easing]}`
    )
    .join(', ');
};

// Helper to get Evo color with opacity
export const withOpacity = (cssVar: string, opacity: number) => {
  return `hsl(var(${cssVar}) / ${opacity})`;
};
